<template>
  <div class="relative h-screen w-full bg-mybg flex flex-col justify-between">
    <!-- Overlay -->
    <div v-if="showShareOverlay || showPrslsOverlay" class="absolute z-40 top-0 left-0 w-screen h-screen bg-black bg-opacity-50 flex pt-16 justify-center">
      <ShareOverlay
        v-if="showShareOverlay"
        :episode="episode"
        @close="showShareOverlay = false"
        @shared="onShared" />

      <PrslsOverlay
        v-if="showPrslsOverlay"
        @close="showPrslsOverlay = false"
        @click-lets-go="trackEvent('episode-view-click-lets-go')" />
    </div>

    <!-- hero section -->
    <div v-if="episode"
      class="p-5 md:p-10 pt-10 max-w-5xl mx-auto overflow-y-scroll
             grid grid-cols-1 justify-items-center">
      <div class="flex w-full justify-center md:justify-start">
        <img class="h-28 w-28 md:h-36 md:w-36 lg:h-52 lg:w-52 rounded-md mr-2"
          loading="lazy"
          :srcset="`
            https://res.cloudinary.com/realworldserverless/image/upload/f_auto,c_scale,h_112/website/logo-ep${episode.episode_number}.jpg 112w,
            https://res.cloudinary.com/realworldserverless/image/upload/f_auto,c_scale,h_144/website/logo-ep${episode.episode_number}.jpg 144w,
            https://res.cloudinary.com/realworldserverless/image/upload/f_auto,c_scale,h_208/website/logo-ep${episode.episode_number}.jpg 208w
          `"
          sizes="(max-width: 768px) 112px,
                 (max-width: 1024px) 144px,
                 208px"
          :src="episode.artwork_url">

        <div>
          <!-- metadata -->
          <div class="my-2 flex flex-col md:flex-row text-sm text-gray-600">
            <div class="flex items-center md:mr-3">
              <div class="w-6">
                <svg class="h-4 fill-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                  <path d="M224 0C100.3 0 0 100.3 0 224c0 92.22 55.77 171.4 135.4 205.7c-3.48-20.75-6.17-41.59-6.998-58.15C80.08 340.1 48 285.8 48 224c0-97.05 78.95-176 176-176s176 78.95 176 176c0 61.79-32.08 116.1-80.39 147.6c-.834 16.5-3.541 37.37-7.035 58.17C392.2 395.4 448 316.2 448 224C448 100.3 347.7 0 224 0zM224 312c-32.88 0-64 8.625-64 43.75c0 33.13 12.88 104.3 20.62 132.8C185.8 507.6 205.1 512 224 512s38.25-4.375 43.38-23.38C275.1 459.9 288 388.8 288 355.8C288 320.6 256.9 312 224 312zM224 280c30.95 0 56-25.05 56-56S254.1 168 224 168S168 193 168 224S193 280 224 280zM368 224c0-79.53-64.47-144-144-144S80 144.5 80 224c0 44.83 20.92 84.38 53.04 110.8c4.857-12.65 14.13-25.88 32.05-35.04C165.1 299.7 165.4 299.7 165.6 299.7C142.9 282.1 128 254.9 128 224c0-53.02 42.98-96 96-96s96 42.98 96 96c0 30.92-14.87 58.13-37.57 75.68c.1309 .0254 .5078 .0488 .4746 .0742c17.93 9.16 27.19 22.38 32.05 35.04C347.1 308.4 368 268.8 368 224z"/>
                </svg>
              </div>
              <span>Real-World Serverless podcast</span>
            </div>

            <div class="flex items-center md:mr-3">
              <div class="w-6">
                <svg class="h-4 fill-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                  <path d="M160 32V64H288V32C288 14.33 302.3 0 320 0C337.7 0 352 14.33 352 32V64H400C426.5 64 448 85.49 448 112V160H0V112C0 85.49 21.49 64 48 64H96V32C96 14.33 110.3 0 128 0C145.7 0 160 14.33 160 32zM0 192H448V464C448 490.5 426.5 512 400 512H48C21.49 512 0 490.5 0 464V192zM80 256C71.16 256 64 263.2 64 272V368C64 376.8 71.16 384 80 384H176C184.8 384 192 376.8 192 368V272C192 263.2 184.8 256 176 256H80z"/>
                </svg>
              </div>
              <span>{{episode.date}}</span>
            </div>

            <div class="flex items-center md:mr-3">
              <div class="w-6">
                <svg class="h-4 fill-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512">
                  <path d="M55.1 56.04C55.1 42.78 66.74 32.04 79.1 32.04H111.1C125.3 32.04 135.1 42.78 135.1 56.04V176H151.1C165.3 176 175.1 186.8 175.1 200C175.1 213.3 165.3 224 151.1 224H71.1C58.74 224 47.1 213.3 47.1 200C47.1 186.8 58.74 176 71.1 176H87.1V80.04H79.1C66.74 80.04 55.1 69.29 55.1 56.04V56.04zM118.7 341.2C112.1 333.8 100.4 334.3 94.65 342.4L83.53 357.9C75.83 368.7 60.84 371.2 50.05 363.5C39.26 355.8 36.77 340.8 44.47 330.1L55.59 314.5C79.33 281.2 127.9 278.8 154.8 309.6C176.1 333.1 175.6 370.5 153.7 394.3L118.8 432H152C165.3 432 176 442.7 176 456C176 469.3 165.3 480 152 480H64C54.47 480 45.84 474.4 42.02 465.6C38.19 456.9 39.9 446.7 46.36 439.7L118.4 361.7C123.7 355.9 123.8 347.1 118.7 341.2L118.7 341.2zM512 64C529.7 64 544 78.33 544 96C544 113.7 529.7 128 512 128H256C238.3 128 224 113.7 224 96C224 78.33 238.3 64 256 64H512zM512 224C529.7 224 544 238.3 544 256C544 273.7 529.7 288 512 288H256C238.3 288 224 273.7 224 256C224 238.3 238.3 224 256 224H512zM512 384C529.7 384 544 398.3 544 416C544 433.7 529.7 448 512 448H256C238.3 448 224 433.7 224 416C224 398.3 238.3 384 256 384H512z"/>
                </svg>
              </div>
              <span>episode {{episode.episode_number}}</span>
            </div>

            <div class="flex items-center md:mr-3">
              <div class="w-6">
                <svg class="h-4 fill-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                  <path d="M272 0C289.7 0 304 14.33 304 32C304 49.67 289.7 64 272 64H256V98.45C293.5 104.2 327.7 120 355.7 143L377.4 121.4C389.9 108.9 410.1 108.9 422.6 121.4C435.1 133.9 435.1 154.1 422.6 166.6L398.5 190.8C419.7 223.3 432 262.2 432 304C432 418.9 338.9 512 224 512C109.1 512 16 418.9 16 304C16 200 92.32 113.8 192 98.45V64H176C158.3 64 144 49.67 144 32C144 14.33 158.3 0 176 0L272 0zM248 192C248 178.7 237.3 168 224 168C210.7 168 200 178.7 200 192V320C200 333.3 210.7 344 224 344C237.3 344 248 333.3 248 320V192z"/>
                </svg>
              </div>
              <span>{{episode.length}}</span>
            </div>
          </div>
          <h1 class="hidden md:block text-lg md:text-3xl tracking-tight font-bold">{{ episode.title }}</h1>
        </div>
      </div>

      <h1 class="text-lg mt-2 md:hidden tracking-tight font-bold">{{ episode.title }}</h1>

      <div class="mt-5 md:mt-10" v-html="innerHtml"></div>

      <ListenOn />

      <Transcript
        :episodeNumber="episode.episode_number"
        @goto="jumpToTimestamp" />
    </div>

    <!-- Prsls ad button -->
    <div class="flex absolute top-0 left-0 w-screen justify-end">
      <a href="#" class="mr-4 -m-2 bg-myred pt-4 pb-2 px-4 rounded-lg text-white font-bold shadow-sm"
         @click="showPrslsOverlay = true">
        Level up your serverless skills
      </a>
    </div>

    <PlayingEpisode ref="player"
      :episode="episode"
      @share="shareEpisode"
      @download="trackEvent('episode-view-click-download')" />
  </div>
</template>

<script>
import { fetchEpisodes } from '../libs/buzzsprout'
import PlayingEpisode from '../components/PlayingEpisode.vue'
import ShareOverlay from '../components/ShareOverlay.vue'
import PrslsOverlay from '../components/PrslsOverlay.vue'
import ListenOn from '../components/ListenOn.vue'
import Transcript from '../components/Transcript.vue'
import { trackFathomGoal } from '../libs/fathom'

export default {
  components: {
    PlayingEpisode,
    ListenOn,
    Transcript,
    ShareOverlay,
    PrslsOverlay
  },
  data () {
    return {
      episode: undefined,
      innerHtml: undefined,
      showPrslsOverlay: false,
      showShareOverlay: false
    }
  },
  async mounted () {
    const episodes = await fetchEpisodes()
    const episodeNumber = parseInt(this.$route.params.number)
    const episode = episodes.find(x => x.episode_number === episodeNumber)
    if (!episode) {
      console.warn(`episode ${episodeNumber} not found`)
      this.$router.push('/')
    } else {
      this.episode = episode

      const innerHtml = episode.description
        .replace(/<ul>/g, '<ul class="list-disc list-inside">')
        .replace(/<a/g, '<a class="underline"')
      const idx = innerHtml.indexOf('Opening theme song')
      this.innerHtml = innerHtml.substring(0, idx)
    }
  },
  methods: {
    shareEpisode () {
      this.sharingEpisode = this.episode
      this.showShareOverlay = true
      this.trackEvent('episode-view-click-share')
    },

    onShared (platform) {
      this.trackEvent(`shared-${platform}`)
    },

    jumpToTimestamp (seconds) {
      this.$refs.player.jumpToTimestamp(seconds)
    },

    trackEvent (eventName) {
      trackFathomGoal(eventName)
    }
  }
}
</script>
